/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable prefer-template */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { ProfileField, Label } from '../../../types/profile';
import useDelayedEffect from '../../../hooks/useDelayedEffect';
import { capitalizeFirstLetter } from '../../../utils/stringUtils';

interface PhoneNumberInputProps {
  field: ProfileField;
  setField: (field: ProfileField) => void;
  canBeEmpty?: boolean;
  mandatory?: boolean;
  validate?: boolean;
  showLabelSelector?: boolean;
}
export default function PhoneNumberInput({
  field,
  setField,
  canBeEmpty = false,
  mandatory = false,
  validate = false,
  showLabelSelector = false,
}: PhoneNumberInputProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');

  useDelayedEffect(() => {
    if (!validate) return;
    if (!field.phoneNumber?.prefix && !field.phoneNumber?.suffix && !field.phoneNumber?.countryCode) {
      setError(canBeEmpty ? '' : t('invalid.empty'));
      return;
    }
    setError(
      parsePhoneNumber(`${field.phoneNumber?.prefix}${field.phoneNumber?.suffix}`)?.isValid()
        ? ''
        : t('invalid.invalid'),
    );
  }, [`${field.phoneNumber?.prefix}${field.phoneNumber?.suffix}`, validate]);

  const handleChange = (s: string): void => {
    if (!s) {
      setField({ ...field, phoneNumber: { prefix: '', suffix: '', countryCode: '' } });
      return;
    }
    const number = parsePhoneNumber(s);
    if (!number || !number.isValid()) {
      setField({ ...field, phoneNumber: { prefix: s, suffix: '', countryCode: '' } });
      return;
    }
    const prefix = `+${number.countryCallingCode}`;

    const suffix = s.replace(prefix, '');

    setField({
      ...field,
      phoneNumber: {
        countryCode: number.country!,
        prefix,
        suffix,
      },
    });
  };

  return (
    <div className="relative flex flex-col">
      <label htmlFor={field.id} className="mb-1 font-medium">
        {t('form.phone.phone')} {mandatory ? '*' : ''}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <PhoneInput
          autoFocus
          autoComplete="tel"
          className={classNames('flex-1 pl-2', { invalid: error })}
          international
          countryCallingCodeEditable
          defaultCountry="NL"
          placeholder={capitalizeFirstLetter(t('dataType.phoneNumber'))}
          onChange={handleChange}
          value={`${field.phoneNumber?.prefix || ''}${field.phoneNumber?.suffix || ''}`}
        />
        {showLabelSelector && (
          <select
            className="h-10 w-full rounded-lg border px-2 text-gray-600 focus:ring-0 focus:ring-transparent sm:w-32"
            value={field.label || Label.GENERAL}
            onChange={(e) => setField({ ...field, label: e.target.value as Label })}>
            <option value={Label.GENERAL}>{t('page.profile.labels.general')}</option>
            <option value={Label.PRIVATE}>{t('page.profile.labels.private')}</option>
            <option value={Label.WORK}>{t('page.profile.labels.work')}</option>
          </select>
        )}
      </div>
      <p className="absolute right-1 top-0 text-sm text-error transition-all">{error}</p>
    </div>
  );
}
